import { useQuery } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { TournamentPlayerEntryResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The caller's entries SCOPED to one tournament (EPIC 1.2 multi-event
 * registration). GET /tournaments/{id}/registrations/mine →
 * TournamentPlayerEntryResponse[] —
 * every entry the caller has in THIS tournament, with LIVE status + hydrated
 * event/draw/partner names (fixes the live Bug A: a withdrawn entry shown as
 * active; Bug B: null event names). AUTH-gated and SERVER-SCOPED to the token
 * user (the route resolves the caller from the token; no uid is threaded), so an
 * anonymous viewer never triggers it. Fires ONLY when logged-in AND a tournamentId.
 *
 * NOTE on naming: the design doc (§8) refers to this as `useMyRegistrations`, but
 * the no-arg cross-tournament `useMyRegistrations()` (GET /users/me/registrations,
 * backing the "My entries" history page) already owns that name and is NOT removed
 * by this epic. To avoid a same-directory function-name collision while delivering
 * the exact tournament-scoped endpoint + query key the design specifies, this
 * tournament-scoped variant is `useTournamentRegistrations(tournamentId)`. The
 * GREEN/cleanup step deletes the SINGULAR getMyRegistration(/registrations/me) +
 * useMyRegistration and migrates TournamentRegistrationStatus onto this list —
 * that is the clean migration, distinct from the cross-tournament list.
 *
 * Its OWN cache slot + query (singular "tournament" prefix → registrationsMine)
 * so this read is isolated and never shares a slot with the cross-tournament list.
 * A register/withdraw mutation invalidates this key so the per-event row reflects
 * the server's refetched truth — never an optimistic local flip.
 */
export function useTournamentRegistrations(tournamentId: string | undefined) {
  const { user } = useAuth();
  return useQuery<TournamentPlayerEntryResponse[]>({
    queryKey: [
      ...queryKeys.tournaments.registrationsMine(tournamentId ?? ""),
      user?.uid,
    ],
    queryFn: () =>
      tournamentApi.getTournamentRegistrationsMine(tournamentId as string),
    enabled: Boolean(user && tournamentId),
  });
}
